/**
 * Select Input Component
 * =====================
 * 
 * Form input template component for rendering dropdown select fields with validation.
 * This component is designed to be used within individual form components to provide
 * a consistent select interface using Ionic's select component with action sheet interface.
 * 
 * Core Features:
 * - Ionic select component with action sheet interface
 * - Dynamic option population from array
 * - Form validation integration with error display
 * - Customizable placeholder text
 * - Disabled state support
 * - Touch state management for validation display
 * 
 * Input Properties:
 * - control: AbstractControl for the specific select field
 * - label: Field label with required indicator
 * - options: Array of selectable options
 * - placeholder: Custom placeholder text
 * - disabled: Whether the select is disabled
 * - position: Label position (stacked, fixed, floating)
 * 
 * Validation Features:
 * - Required field validation
 * - Pattern validation support
 * - Custom error messages for validation failures
 * - Visual error states and styling
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';

@Component({
  selector: 'app-select-input',
  template: `
    <ng-container *ngIf="control">
    <ion-label [position]="position" class="form-label">
        <strong>{{ label }}:</strong>
        <ion-text *ngIf="requiredField" color="danger">*</ion-text>
      </ion-label>
      <ion-item class="ion-no-padding" lines="none" class="form-input-border">
      <ion-select
        [id]="controlName"
        [formControl]="formControl
        "
        [placeholder]="placeholder"
        [disabled]="disabled"
        interface="action-sheet"
        [class.ion-invalid]="hasError"
        [class.ion-touched]="control.touched"
        [errorText]="getErrorMessage()">

        <ion-select-option *ngFor="let option of options" [value]="option">
        
          {{ option }}
        </ion-select-option>
      </ion-select>
      
      <!-- Error Messages -->
      <div *ngIf="hasError" class="error-container">
        <ion-text color="danger">
          <small *ngIf="control.errors?.['required']">
            Please select an option
          </small>
          <small *ngIf="control.errors?.['pattern']">
            Field selection is invalid
          </small>
          <!-- Add more error types as needed for selects -->
        </ion-text>
      </div>
      </ion-item>
    </ng-container>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
    
    ion-select {
      width: 100%;
      --placeholder-color: var(--ion-color-medium);
      --placeholder-opacity: 1;
    }
    

    /* Optional: Style invalid selects */
    ion-select.ion-invalid.ion-touched {
      --border-color: var(--ion-color-danger);
      --color: var(--ion-color-danger);
    }
  `]
})
export class SelectInputComponent implements OnChanges, OnInit {
  @Input() control!: AbstractControl | null;
  @Input() label: string = '';
  @Input() requiredField: boolean = false;
  @Input() placeholder: string = 'Select an option';
  @Input() options: Array<string> = [];
  @Input() disabled: boolean = false;
  @Input() position: 'stacked' | 'fixed' | 'floating' = 'floating';

  controlName: string = '';
  formControl: FormControl = new FormControl();

  /**
   * Computed property to check if control has errors and is touched.
   * Used to determine when to show error states and styling.
   * 
   * @returns True if the control has validation errors and has been touched
   */
  get hasError(): boolean {
    return !!(this.control?.invalid && this.control?.touched);
  }

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Sets up the form control and initializes the component.
   */
  ngOnInit(): void {
    if (this.control) {
      this.formControl = this.control as FormControl;
    }
  }

  /**
   * Lifecycle hook that responds to input property changes.
   * Handles changes to the control property and updates the form control.
   * 
   * @param changes - Object containing information about changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if ('control' in changes && this.control) {
      this.formControl = this.control as FormControl;
    }
  }

 

  /**
   * Gets the appropriate error message based on validation errors.
   * Provides user-friendly error messages for different validation failures.
   * 
   * @returns String containing the appropriate error message
   */
  getErrorMessage(): string {
    if (!this.hasError || !this.control?.errors) {
      return '';
    }
    const errors = this.control.errors;
    if (errors['pattern']) {
      return `Field selection is invalid`;
    }

    if (errors['required']) {
      return `Please select an option`;
    }

    return `${this.label} is invalid`;
  }


}